import React, { useState, useMemo, useContext, useCallback } from 'react';
import dayjs from 'dayjs';
import { getTestId } from '../hooks/useTestId';

import useResourceLoader from '../util/useResourceLoader';
import UWEEntityApi from '../api/UWEEntityApi';
import { DATETIME_FORMAT } from '../constants';
import Notification from '../components/Notification';
import { EmptyState } from '../components/Icons/EmptyStates';
import { toDrDateTime } from '../util/dates';
import Loader from '../components/Loader';
import { AuthContext } from '../context/AuthContext';
import UWEFormContext from '../context/UWEFormContext';
import { Trans, useLingui } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import UserIcon from '../components/UserIcon';
import Icon from 'src/components/Icon';

function UWEEntityNote({ entity, readOnly, wfAccessToken }) {
  const entityId = entity?.id;
  const auth = useContext(AuthContext);
  const { pendingNotes, setPendingNotes } = useContext(UWEFormContext);

  const [newNote, setNewNote] = useState('');
  const [notes, loadingNotes, error, setNotes] = useResourceLoader(
    () => (entityId ? UWEEntityApi.getUWEEntityNotes({ entityId, wfAccessToken }) : []),
    [entityId],
    []
  );

  const mergedNotes = useMemo(
    () =>
      [
        ...(notes || []).map(({ createdAt, message, user }) => ({
          date: dayjs.utc(createdAt).local(),
          createdAt,
          message,
          user,
        })),
        ...(pendingNotes || []).map(({ createdAt, message, user }) => ({
          isPending: true,
          date: dayjs(createdAt),
          createdAt,
          message,
          user: { name: user },
        })),
      ].sort((b, a) => a.date.diff(b.date)),
    [notes, pendingNotes]
  );

  const submitNewNote = useCallback(async () => {
    if (newNote) {
      if (entityId) {
        const addedNote = await UWEEntityApi.createUWEEntityNote({ entityId, note: newNote, wfAccessToken });
        setNotes(notes => [addedNote, ...notes]);
      } else {
        setPendingNotes(pendingNotes => [
          ...(pendingNotes ?? []),
          {
            message: newNote,
            createdAt: new Date().toISOString(),
            user: `${auth?.user?.user?.firstName} ${auth?.user?.user?.lastName}`,
          },
        ]);
      }

      setNewNote('');
    }
  }, [newNote, UWEEntityApi, auth, entityId, setNotes, setNewNote, setPendingNotes]);

  return loadingNotes ? (
    <Loader fullscreen>
      <Trans>Loading Notes...</Trans>
    </Loader>
  ) : (
    <>
      {error ? <Notification color="danger">{JSON.stringify(error)}</Notification> : null}
      <UWEEntityNoteSidePanelComponent
        readOnly={readOnly}
        newNote={newNote}
        setNewNote={setNewNote}
        submitNewNote={submitNewNote}
        notes={mergedNotes}
      />
    </>
  );
}

function NoteCard({ isPending, createdAt, message, user }) {
  const { i18n } = useLingui();
  return (
    <div className={`note-card${isPending ? ' note-pending' : ''}`}>
      <div className="note-card-meta">
        <div className="note-avatar">{<UserIcon user={user} size={25} />}</div>
        <div className="note-card-info">
          <span className="note-user">{user?.name || ''}</span>
          <span className="note-date">
            {dayjs
              .utc(createdAt)
              .local()
              .locale(i18n.locale)
              .format('LL, hh:mmA')}
            {isPending ? ' *' : ''}
          </span>
        </div>
      </div>
      <div className="note-text">{message}</div>
    </div>
  );
}

function UWEEntityNoteSidePanelComponent({ readOnly, newNote, setNewNote, submitNewNote, notes }) {
  function onKeyDown(e) {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      submitNewNote();
    }
  }

  return (
    <div className="uweentity-note-sidepanel-component h-full flex flex-col" style={{ minHeight: 0 }}>
      <div className="uweentity-notes-list" style={{ overflow: 'auto', flexGrow: 1, minHeight: 0 }}>
        {!notes.length ? <EmptyState icon="inbox" message={t`No notes yet.`} size={160} /> : null}
        {notes.map((note, idx) => (
          <NoteCard key={idx} {...note} />
        ))}
      </div>
      {!readOnly ? (
        <div className="note-compose" style={{ minHeight: 0 }}>
          <textarea
            className="note-compose-input"
            placeholder={t`Write a note… (Ctrl+Enter to send)`}
            value={newNote}
            onChange={({ target: { value } }) => setNewNote(value)}
            onKeyDown={onKeyDown}
          />
          <div className="flex gap-1" style={{ justifyContent: 'space-between' }}>
            <small className="text-muted">
              <Trans>These notes can be seen with anyone with access to this form.</Trans>
            </small>
            <button className="btn btn-primary note-compose-send nowrap" onClick={submitNewNote} {...getTestId('entity-note', '.', 'add')}>
              <Icon icon="PaperPlaneRight" />
              <Trans> Add Note</Trans>
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default UWEEntityNote;
